import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import * as analyticsApi from '../api/analytics';
import * as channelsApi from '../api/channels';
import * as costsApi from '../api/costs';
import * as invoicesApi from '../api/invoices';
import * as salesApi from '../api/sales';
import * as settingsApi from '../api/settings';
import { currentMonthStr } from '../lib/format';
import { useBusinessId } from '../useBusinessId';
import { useProfitIqContext } from '../context/ProfitIqContext';
import { useAnalyticsParams, useDataMode } from './useSelection';

/**
 * Profit IQ data access.
 *
 * Every read carries the selected business id in its query key. The id never
 * reaches the wire — the backend scopes on the `business` request header — it
 * is there so a business switch lands on an empty cache entry and the screens
 * paint their loader instead of the previous business's figures.
 *
 * An edit invalidates only the cheap reads it changes. `profit-analytics` and
 * `profit-menu-items` are deliberately left alone: both rebuild the window's
 * dashboard to price what they return, and refetching that on every
 * keystroke-sized save is what made saving feel slow. An edit marks them stale
 * instead and the section refreshes them when another tab is opened. A sales
 * import is the exception — it replaces the whole dataset, so it refreshes
 * everything at once.
 */

const keys = {
  analytics: (businessId, params) => ['profit-analytics', businessId, params],
  menuItems: (businessId, params) => ['profit-menu-items', businessId, params],
  channels: (businessId) => ['profit-channels', businessId],
  settings: (businessId) => ['profit-settings', businessId],
  costMonths: (businessId) => ['profit-cost-months', businessId],
  costs: (businessId, month) => ['profit-costs', businessId, month],
  costDefaults: (businessId) => ['profit-costs', businessId, 'defaults'],
  invoices: (businessId, month) => ['profit-invoices', businessId, month],
  sales: (businessId) => ['profit-sales', businessId],
};

/** Invalidates every read an edit can move. */
function useInvalidate() {
  const qc = useQueryClient();
  return (...prefixes) => {
    prefixes.forEach((prefix) =>
      qc.invalidateQueries({ queryKey: [prefix], exact: false })
    );
  };
}

/**
 * Records that an edit has moved figures the dashboard reports. The refresh
 * itself is left to the section shell, which runs it when another tab is
 * opened, so editing never waits for a dashboard rebuild.
 */
function useMarkStale() {
  const { markDashboardStale } = useProfitIqContext();
  return markDashboardStale;
}

/**
 * The analytics params for one calendar month, independent of the date-bar
 * selection. Cost lines are stored per month, so the costs editor compares them
 * against the revenue of the month being edited rather than whatever window the
 * date bar happens to show.
 *
 * The current month resolves through `thismonth`, which the API measures to
 * date and reports as elapsed days; asking for it by name would return the full
 * calendar month and prorate part-month revenue over all 30-31 days.
 */
function monthParams(month, dataMode) {
  return month === currentMonthStr()
    ? { range: 'thismonth', customDays: 30, month, dataMode }
    : { range: 'month', customDays: 30, month, dataMode };
}

export function useAnalytics() {
  const businessId = useBusinessId();
  const params = useAnalyticsParams();
  return useQuery({
    queryKey: keys.analytics(businessId, params),
    queryFn: () => analyticsApi.getAnalytics(params),
    enabled: !!businessId,
  });
}

export function useMonthAnalytics(month) {
  const businessId = useBusinessId();
  const { dataMode } = useDataMode();
  const params = monthParams(month, dataMode);
  return useQuery({
    queryKey: keys.analytics(businessId, params),
    queryFn: () => analyticsApi.getAnalytics(params),
    enabled: !!businessId && !!month,
  });
}

/**
 * How many items a page of the list holds.
 *
 * Stated rather than left to the endpoint's own default, because the screen has
 * to be able to tell whether the page the dashboard already sent is the whole
 * first page of this list. `MENU_ITEMS_PAGE_SIZE` on the server is the same
 * number; the endpoint accepts any limit up to 200.
 */
export const MENU_ITEMS_PAGE_SIZE = 20;

/**
 * The menu list, a page at a time.
 *
 * The backend ranks and pages it, so the whole menu never crosses the wire: it
 * used to, in full, on a screen that shows ten rows. `sort` and `order` are
 * stated rather than left to the endpoint's own default, because the dashboard's
 * first page is ranked by revenue and this list continues it.
 *
 * `firstPage` is that dashboard page, when the caller could establish it is this
 * list's first page too (see `seedFirstPage`). Passing it means the rows are on
 * screen from the dashboard read alone, with no request of this endpoint until
 * the reader asks for more — which is the whole point of `/analytics` returning
 * a page of items rather than a count. `firstPageFetchedAt` is when the
 * dashboard read landed: the seed is exactly as fresh as that read, so it goes
 * stale with it instead of being refetched straight away.
 */
export function useMenuItemsPages({
  sort = 'revenue',
  order = 'desc',
  category,
  search,
  enabled = true,
  firstPage,
  firstPageFetchedAt,
} = {}) {
  const businessId = useBusinessId();
  const params = useAnalyticsParams();
  const listParams = { ...params, sort, order, category, search };
  return useInfiniteQuery({
    queryKey: keys.menuItems(businessId, listParams),
    queryFn: ({ pageParam }) =>
      analyticsApi.getMenuItems({
        ...listParams,
        page: pageParam,
        limit: MENU_ITEMS_PAGE_SIZE,
      }),
    initialPageParam: 1,
    // A short page is the last one, which is also how a page past the end reads.
    getNextPageParam: (last, pages) =>
      last.items.length < last.limit ? undefined : pages.length + 1,
    enabled: !!businessId && enabled,
    initialData: firstPage
      ? { pages: [firstPage], pageParams: [1] }
      : undefined,
    initialDataUpdatedAt: firstPage ? firstPageFetchedAt : undefined,
  });
}

/**
 * The dashboard's item page as this list's first page, or `undefined` when the
 * two cannot be shown to be the same page.
 *
 * `/analytics` carries the first `MENU_ITEMS_PAGE_SIZE` items that **sold**,
 * ranked by revenue. This list is the whole menu — everything that sold plus the
 * catalog rows that sold nothing — ranked the same way. The two therefore share
 * a first page only while nothing that sold nothing could have reached it, which
 * is exactly when the page is full and its last item earned something: an item
 * with no revenue ties with every unsold one, and the tie is broken by id, so
 * the dashboard's list and the whole menu can order those two differently.
 *
 * Returning `undefined` rather than a short page matters. A seed of 7 rows would
 * be read as the last page by `getNextPageParam`, and the rest of the menu would
 * never load.
 */
export function seedFirstPage(dashboard) {
  const items = dashboard?.items;
  if (!items || items.length !== MENU_ITEMS_PAGE_SIZE) return undefined;

  const last = items[items.length - 1];
  if (last.salesPrice * last.unitsSold <= 0) return undefined;

  return {
    items,
    total: dashboard.itemsTotal,
    page: 1,
    limit: MENU_ITEMS_PAGE_SIZE,
  };
}

export function useChannels() {
  const businessId = useBusinessId();
  return useQuery({
    queryKey: keys.channels(businessId),
    queryFn: channelsApi.getChannels,
    enabled: !!businessId,
  });
}

export function useProfitSettings() {
  const businessId = useBusinessId();
  return useQuery({
    queryKey: keys.settings(businessId),
    queryFn: settingsApi.getProfitSettings,
    enabled: !!businessId,
  });
}

export function useCostMonths() {
  const businessId = useBusinessId();
  return useQuery({
    queryKey: keys.costMonths(businessId),
    queryFn: costsApi.getCostMonths,
    enabled: !!businessId,
  });
}

export function useCosts(month) {
  const businessId = useBusinessId();
  return useQuery({
    queryKey: keys.costs(businessId, month),
    queryFn: () => costsApi.getCosts(month),
    enabled: !!businessId && !!month,
  });
}

export function useCostDefaults(enabled = true) {
  const businessId = useBusinessId();
  return useQuery({
    queryKey: keys.costDefaults(businessId),
    queryFn: costsApi.getCostDefaults,
    enabled: !!businessId && enabled,
  });
}

export function useInvoices(month) {
  const businessId = useBusinessId();
  return useQuery({
    queryKey: keys.invoices(businessId, month),
    queryFn: () => invoicesApi.getInvoices(month),
    enabled: !!businessId && !!month,
  });
}

export function useSalesDatasets() {
  const businessId = useBusinessId();
  return useQuery({
    queryKey: keys.sales(businessId),
    queryFn: salesApi.getSalesDatasets,
    enabled: !!businessId,
  });
}

export function useUpdateMenuItem() {
  const markStale = useMarkStale();
  // The mode decides whether the saved cost is keyed by MMS product id or by
  // item name, so the write states the same source the row was read from.
  const { dataMode } = useDataMode();
  return useMutation({
    mutationFn: ({ id, body }) =>
      analyticsApi.updateMenuItem(id, body, dataMode),
    // Both of this endpoint's reads are the expensive ones, so a saved cost
    // invalidates nothing and the row keeps the figure just typed.
    onSuccess: markStale,
  });
}

export function useUpdateChannel() {
  const invalidate = useInvalidate();
  const markStale = useMarkStale();
  return useMutation({
    mutationFn: ({ id, body }) => channelsApi.updateChannel(id, body),
    onSuccess: () => {
      invalidate('profit-channels');
      markStale();
    },
  });
}

/**
 * Profit IQ's business-level knobs (tax rate, card mix) live in the
 * `profitPulseSettings` sidecar collection in api01, so what the mobile app
 * calls "update business" is a settings patch.
 */
export function useUpdateProfitSettings() {
  const invalidate = useInvalidate();
  const markStale = useMarkStale();
  return useMutation({
    mutationFn: (body) => settingsApi.updateProfitSettings(body),
    onSuccess: () => {
      invalidate('profit-settings');
      markStale();
    },
  });
}

export function usePutCosts() {
  const invalidate = useInvalidate();
  const markStale = useMarkStale();
  return useMutation({
    mutationFn: ({ month, body }) => costsApi.putCosts(month, body),
    onSuccess: () => {
      invalidate('profit-costs');
      markStale();
    },
  });
}

export function usePutCostDefaults() {
  const invalidate = useInvalidate();
  const markStale = useMarkStale();
  return useMutation({
    mutationFn: (body) => costsApi.putCostDefaults(body),
    onSuccess: () => {
      invalidate('profit-costs');
      markStale();
    },
  });
}

export function useSetCostDefaultsFromMonth() {
  const invalidate = useInvalidate();
  const markStale = useMarkStale();
  return useMutation({
    mutationFn: (month) => costsApi.setCostDefaultsFromMonth(month),
    onSuccess: () => {
      invalidate('profit-costs');
      markStale();
    },
  });
}

export function useCopyCosts() {
  const invalidate = useInvalidate();
  const markStale = useMarkStale();
  return useMutation({
    mutationFn: ({ from, to }) => costsApi.copyCosts(from, to),
    onSuccess: () => {
      invalidate('profit-costs');
      markStale();
    },
  });
}

export function useCreateInvoice() {
  const invalidate = useInvalidate();
  const markStale = useMarkStale();
  return useMutation({
    mutationFn: (body) => invoicesApi.createInvoice(body),
    onSuccess: () => {
      invalidate('profit-invoices', 'profit-costs');
      markStale();
    },
  });
}

export function useDeleteInvoice() {
  const invalidate = useInvalidate();
  const markStale = useMarkStale();
  return useMutation({
    mutationFn: (id) => invoicesApi.deleteInvoice(id),
    onSuccess: () => {
      invalidate('profit-invoices', 'profit-costs');
      markStale();
    },
  });
}

export function usePreviewSales() {
  return useMutation({
    mutationFn: ({ csv, source }) => salesApi.previewSales(csv, source),
  });
}

export function useImportSales() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body) => salesApi.importSales(body),
    onSuccess: () =>
      invalidate(
        'profit-sales',
        'profit-analytics',
        'profit-channels',
        'profit-menu-items',
        'profit-costs'
      ),
  });
}
